import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { AppItem } from '@/hooks/app-library/use-app-favorites';
import { useDragContext } from '@/contexts/drag/DragContext';
import AppCard from './AppCard';

interface PinnedAppSlotProps {
  position: 1 | 2 | 3 | 4;
  app: AppItem | null;
}

const DraggableAppCard: React.FC<{ app: AppItem; position: 1 | 2 | 3 | 4 }> = ({ app, position }) => {
  const { isDragging: globalIsDragging } = useDragContext();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `pinned-${position}`,
    data: { app, position }
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `position-${position}`,
    data: { position }
  });

  // Combine refs for both dragging and dropping
  const combinedRef = (node: HTMLElement | null) => {
    setNodeRef(node);
    setDropRef(node);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: globalIsDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <motion.div
      ref={combinedRef}
      style={style}
      layoutId={`pinned-app-${app.id}`}
      whileHover={{ scale: 1.02 }}
      transition={{ 
        layout: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] },
        duration: 0.2, 
        ease: [0.25, 0.1, 0.25, 1.0] 
      }}
      className={isOver ? 'ring-2 ring-blue-400 ring-opacity-50' : ''}
    >
      <AppCard 
        app={app} 
        isPinnedPreview={true}
        pinnedPosition={position}
        dragHandleRef={setActivatorNodeRef}
        dragListeners={listeners}
        dragAttributes={attributes}
      />
    </motion.div>
  );
};

const EmptySlotPlaceholder: React.FC<{ position: 1 | 2 | 3 | 4 }> = ({ position }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `position-${position}`,
    data: { position }
  });

  return (
    <motion.div 
      ref={setNodeRef}
      className={`h-32 rounded-lg border-2 border-dashed flex items-center justify-center ${
        isOver 
          ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/20' 
          : 'border-gray-200 dark:border-gray-700'
      }`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
      whileHover={{ scale: 1.02, borderColor: 'rgb(147 197 253)' }}
    >
      <div className="text-center text-muted-foreground">
        <p className="text-sm font-medium mb-1">Position {position}</p>
        <p className="text-xs">No app pinned</p>
      </div>
    </motion.div>
  );
};

const PinnedAppSlot: React.FC<PinnedAppSlotProps> = ({ position, app }) => {
  return (
    <div className="rounded-lg">
      {app ? (
        <DraggableAppCard app={app} position={position} />
      ) : (
        <EmptySlotPlaceholder position={position} />
      )}
    </div>
  );
};

export default PinnedAppSlot; 